import React, { useState, useEffect, useRef } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { getLessonById } from '../services/courseService';
import ReactMarkdown from 'react-markdown';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { atomDark } from 'react-syntax-highlighter/dist/esm/styles/prism';
import rehypeRaw from 'rehype-raw';

const LessonDetail = () => {
  const { lessonId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [lesson, setLesson] = useState(null);
  const [error, setError] = useState(null);
  const [activeSection, setActiveSection] = useState(null);
  const contentRef = useRef(null);

  // Extract headings from content for table of contents
  const [headings, setHeadings] = useState([]);

  useEffect(() => {
    const loadLesson = async () => {
      try {
        setLoading(true);
        const lessonData = await getLessonById(lessonId);
        
        if (!lessonData) {
          setError('Lesson not found');
          return;
        }
        
        setLesson(lessonData);
        
        // Extract headings for table of contents
        const headingRegex = /^(#{1,3})\s+(.+)$/gm;
        const matches = [...lessonData.content.matchAll(headingRegex)];
        
        const extractedHeadings = matches.map((match, index) => {
          const level = match[1].length;
          const text = match[2];
          const id = `heading-${index}`;
          return { level, text, id };
        });
        
        setHeadings(extractedHeadings);
      } catch (err) {
        console.error('Error loading lesson:', err);
        setError('Failed to load lesson data');
      } finally {
        setLoading(false);
      }
    };

    loadLesson();
  }, [lessonId]);

  useEffect(() => {
    // Observe headings to update active section
    if (!contentRef.current) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setActiveSection(entry.target.id);
          }
        });
      },
      { rootMargin: '-100px 0px -80% 0px' }
    );

    // Observe all headings
    const headingElements = contentRef.current.querySelectorAll('h1, h2, h3');
    headingElements.forEach((element) => {
      observer.observe(element);
    });

    return () => {
      headingElements.forEach((element) => {
        observer.unobserve(element);
      });
    };
  }, [lesson]);

  if (loading) {
    return (
      <div className="flex flex-col justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary-600 mb-4"></div>
        <p className="text-gray-600 dark:text-gray-400 animate-pulse">Loading lesson content...</p>
      </div>
    );
  }

  // Handle error state
  if (error) {
    return (
      <div className="text-center py-12">
        <div className="bg-red-50 dark:bg-red-900/20 p-8 rounded-xl max-w-lg mx-auto mb-8">
          <svg className="w-16 h-16 text-red-500 dark:text-red-400 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
          </svg>
          <h2 className="text-2xl font-bold text-gray-800 dark:text-white mb-4">{error}</h2>
          <p className="text-gray-600 dark:text-gray-400 mb-6">
            The lesson you're looking for could not be found or there was an error loading it.
          </p>
          <button
            onClick={() => navigate(-1)}
            className="px-6 py-3 bg-primary-600 text-white rounded-md hover:bg-primary-700 transition-colors shadow-md hover:shadow-lg transform hover:-translate-y-1 duration-200"
          >
            Go Back
          </button>
        </div>
      </div>
    );
  }

  // If no lesson data is available
  if (!lesson) {
    return (
      <div className="text-center py-12">
        <div className="bg-gray-50 dark:bg-gray-800/50 p-8 rounded-xl max-w-lg mx-auto mb-8">
          <svg className="w-16 h-16 text-gray-400 dark:text-gray-500 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <h2 className="text-2xl font-bold text-gray-800 dark:text-white mb-4">Lesson Not Found</h2>
          <p className="text-gray-600 dark:text-gray-400 mb-6">
            The lesson you're looking for could not be found.
          </p>
          <button
            onClick={() => navigate('/dashboard')}
            className="px-6 py-3 bg-primary-600 text-white rounded-md hover:bg-primary-700 transition-colors shadow-md hover:shadow-lg transform hover:-translate-y-1 duration-200"
          >
            Return to Dashboard
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="pb-16">
      {/* Lesson Header */}
      <div className="mb-8 relative">
        <div className="absolute inset-0 bg-gradient-to-r from-primary-600/20 to-transparent rounded-xl opacity-60"></div>
        <div className="relative px-6 py-8">
          <div className="flex flex-wrap items-center gap-3 mb-3">
            <Link 
              to={`/dashboard/courses/${lesson.courseId}`}
              className="text-sm text-primary-600 dark:text-primary-400 hover:underline flex items-center bg-white dark:bg-gray-800 px-3 py-1 rounded-full shadow-sm"
            >
              <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
              Back to {lesson.courseTitle}
            </Link>
            <span className="px-3 py-1 bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200 text-xs font-medium rounded-full shadow-sm">
              {lesson.difficulty || 'All Levels'}
            </span>
            <span className="px-3 py-1 bg-primary-100 dark:bg-primary-900/30 text-primary-800 dark:text-primary-300 text-xs font-medium rounded-full shadow-sm">
              {lesson.category || 'AI Learning'}
            </span>
          </div>
          <h1 className="text-3xl md:text-4xl font-heading font-bold text-gray-900 dark:text-white mb-3">
            {lesson.title}
          </h1>
          <div className="flex flex-wrap items-center gap-4 text-sm text-gray-600 dark:text-gray-400">
            <div className="flex items-center">
              <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              {lesson.duration}
            </div>
            {lesson.tags && lesson.tags.length > 0 && (
              <div className="flex flex-wrap gap-2">
                {lesson.tags.map((tag, index) => (
                  <span key={index} className="text-xs bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 px-2 py-1 rounded">
                    #{tag}
                  </span>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
        {/* Table of Contents - Desktop */}
        {headings.length > 0 && (
          <div className="hidden lg:block lg:col-span-1">
            <div className="sticky top-6 bg-white dark:bg-gray-800 rounded-xl shadow-sm p-5 max-h-[calc(100vh-100px)] overflow-y-auto">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center">
                <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h7" />
                </svg>
                Table of Contents
              </h3>
              <nav>
                <ul className="space-y-2 text-sm">
                  {headings.map((heading, index) => (
                    <li 
                      key={index} 
                      className={`
                        ${heading.level === 1 ? '' : heading.level === 2 ? 'ml-3' : 'ml-6'}
                        ${activeSection === heading.id ? 'text-primary-600 dark:text-primary-400 font-medium' : 'text-gray-600 dark:text-gray-400'}
                      `}
                    >
                      <a 
                        href={`#${heading.id}`}
                        className="hover:text-primary-700 dark:hover:text-primary-300 transition-colors flex items-center"
                        onClick={(e) => {
                          e.preventDefault();
                          document.getElementById(heading.id)?.scrollIntoView({
                            behavior: 'smooth'
                          });
                        }}
                      >
                        {activeSection === heading.id && (
                          <span className="w-1 h-4 bg-primary-600 dark:bg-primary-400 rounded-full mr-2"></span>
                        )}
                        {heading.text}
                      </a>
                    </li>
                  ))}
                </ul>
              </nav>
            </div>
          </div>
        )}

        {/* Lesson Content */}
        <div className={`${headings.length > 0 ? 'lg:col-span-3' : 'lg:col-span-4'}`}>
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg p-6 md:p-8 mb-8" ref={contentRef}>
            <div className="prose dark:prose-invert prose-headings:font-heading prose-headings:scroll-mt-20 prose-img:rounded-lg prose-img:shadow-md prose-a:text-primary-600 dark:prose-a:text-primary-400 max-w-none">
              <ReactMarkdown
                rehypePlugins={[rehypeRaw]}
                components={{
                  h1: ({node, ...props}) => {
                    const id = `heading-${headings.findIndex(h => h.text === props.children[0])}`;
                    return <h1 id={id} className="text-3xl font-bold pt-4 border-b border-gray-200 dark:border-gray-700 pb-2 mb-6" {...props} />;
                  },
                  h2: ({node, ...props}) => {
                    const id = `heading-${headings.findIndex(h => h.text === props.children[0])}`;
                    return <h2 id={id} className="text-2xl font-bold pt-4 mt-8 mb-4" {...props} />;
                  },
                  h3: ({node, ...props}) => {
                    const id = `heading-${headings.findIndex(h => h.text === props.children[0])}`;
                    return <h3 id={id} className="text-xl font-bold pt-2 mt-6 mb-3" {...props} />;
                  },
                  p: ({node, ...props}) => {
                    return <p className="leading-relaxed mb-4" {...props} />;
                  },
                  ul: ({node, ...props}) => {
                    return <ul className="list-disc pl-8 mb-6 space-y-2" {...props} />;
                  },
                  ol: ({node, ...props}) => {
                    return <ol className="list-decimal pl-8 mb-6 space-y-2" {...props} />;
                  },
                  li: ({node, ...props}) => {
                    return <li className="pl-2" {...props} />;
                  },
                  blockquote: ({node, ...props}) => {
                    return <blockquote className="border-l-4 border-primary-600 dark:border-primary-400 pl-4 py-2 bg-primary-50 dark:bg-primary-900/20 rounded-r-md italic text-gray-700 dark:text-gray-300 my-6" {...props} />;
                  },
                  code({node, inline, className, children, ...props}) {
                    const match = /language-(\w+)/.exec(className || '')
                    return !inline && match ? (
                      <div className="relative my-6 group">
                        <div className="absolute -top-3 right-3 bg-gray-100 dark:bg-gray-700 px-2 py-1 text-xs font-mono rounded text-gray-500 dark:text-gray-400 z-10">
                          {match[1]}
                        </div>
                        <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity z-10">
                          <button
                            className="text-gray-400 hover:text-gray-600 dark:text-gray-500 dark:hover:text-gray-300"
                            onClick={() => {
                              navigator.clipboard.writeText(String(children).replace(/\n$/, ''));
                            }}
                          >
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                            </svg>
                          </button>
                        </div>
                        <SyntaxHighlighter
                          language={match[1]}
                          style={atomDark}
                          className="rounded-md shadow-lg !mt-0 overflow-hidden !bg-gray-800"
                          showLineNumbers={true}
                          wrapLines={true}
                          {...props}
                        >
                          {String(children).replace(/\n$/, '')}
                        </SyntaxHighlighter>
                      </div>
                    ) : (
                      <code className="bg-gray-100 dark:bg-gray-800 text-gray-800 dark:text-gray-200 px-1.5 py-0.5 rounded-md font-mono text-sm" {...props}>
                        {children}
                      </code>
                    )
                  },
                  table({node, ...props}) {
                    return (
                      <div className="overflow-x-auto my-6">
                        <table className="min-w-full divide-y divide-gray-300 dark:divide-gray-700 border border-gray-300 dark:border-gray-700 rounded-lg" {...props} />
                      </div>
                    )
                  },
                  th({node, ...props}) {
                    return <th className="px-4 py-3 font-semibold text-left bg-gray-100 dark:bg-gray-700 text-gray-900 dark:text-white" {...props} />
                  },
                  td({node, ...props}) {
                    return <td className="px-4 py-3 border-t border-gray-200 dark:border-gray-700" {...props} />
                  },
                  hr({node, ...props}) {
                    return <hr className="my-8 border-gray-200 dark:border-gray-700" {...props} />
                  },
                  img({node, ...props}) {
                    return (
                      <img
                        className="mx-auto my-8 rounded-lg shadow-lg max-h-[500px] object-cover"
                        {...props}
                        loading="lazy"
                      />
                    )
                  }
                }}
              >
                {lesson.content}
              </ReactMarkdown>
            </div>
          </div>

          {/* Table of Contents - Mobile */}
          {headings.length > 0 && (
            <div className="lg:hidden bg-white dark:bg-gray-800 rounded-xl shadow-sm p-5 mb-8">
              <details className="group">
                <summary className="list-none flex justify-between items-center cursor-pointer">
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center">
                    <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h7" />
                    </svg>
                    Table of Contents
                  </h3>
                  <svg className="w-5 h-5 text-gray-500 group-open:rotate-180 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </summary>
                <nav className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
                  <ul className="space-y-2 text-sm">
                    {headings.map((heading, index) => (
                      <li 
                        key={index} 
                        className={`
                          ${heading.level === 1 ? '' : heading.level === 2 ? 'ml-3' : 'ml-6'}
                          ${activeSection === heading.id ? 'text-primary-600 dark:text-primary-400 font-medium' : 'text-gray-600 dark:text-gray-400'}
                        `}
                      >
                        <a 
                          href={`#${heading.id}`}
                          className="hover:text-primary-700 dark:hover:text-primary-300 transition-colors flex items-center"
                          onClick={(e) => {
                            e.preventDefault();
                            document.getElementById(heading.id)?.scrollIntoView({
                              behavior: 'smooth'
                            });
                          }}
                        >
                          {activeSection === heading.id && (
                            <span className="w-1 h-4 bg-primary-600 dark:bg-primary-400 rounded-full mr-2"></span>
                          )}
                          {heading.text}
                        </a>
                      </li>
                    ))}
                  </ul>
                </nav>
              </details>
            </div>
          )}

          {/* Lesson Navigation */}
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm p-5 md:p-6">
            <div className="flex flex-col sm:flex-row justify-between items-center">
              {lesson.previousLessonId ? (
                <Link 
                  to={`/dashboard/lessons/${lesson.previousLessonId}`}
                  className="group flex items-center text-gray-700 dark:text-gray-300 hover:text-primary-600 dark:hover:text-primary-400 transition-colors w-full sm:w-auto mb-4 sm:mb-0"
                >
                  <div className="bg-gray-100 dark:bg-gray-700 rounded-full p-2 mr-3 group-hover:bg-primary-100 dark:group-hover:bg-primary-900/30 transition-colors">
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                    </svg>
                  </div>
                  <div>
                    <div className="text-xs text-gray-500 dark:text-gray-400 mb-1">Previous Lesson</div>
                    <div className="font-medium">Continue Learning</div>
                  </div>
                </Link>
              ) : (
                <div className="w-full sm:w-auto mb-4 sm:mb-0"></div>
              )}

              <Link 
                to={`/dashboard/courses/${lesson.courseId}`}
                className="hidden sm:flex px-5 py-2.5 bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200 rounded-md hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors items-center"
              >
                <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
                </svg>
                Course Overview
              </Link>

              {lesson.nextLessonId ? (
                <Link 
                  to={`/dashboard/lessons/${lesson.nextLessonId}`}
                  className="group flex items-center text-gray-700 dark:text-gray-300 hover:text-primary-600 dark:hover:text-primary-400 transition-colors justify-end w-full sm:w-auto"
                >
                  <div className="text-right">
                    <div className="text-xs text-gray-500 dark:text-gray-400 mb-1">Next Lesson</div>
                    <div className="font-medium">Continue Learning</div>
                  </div>
                  <div className="bg-gray-100 dark:bg-gray-700 rounded-full p-2 ml-3 group-hover:bg-primary-100 dark:group-hover:bg-primary-900/30 transition-colors">
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </div>
                </Link>
              ) : (
                <Link 
                  to={`/dashboard/courses/${lesson.courseId}`}
                  className="group flex items-center text-green-600 dark:text-green-400 hover:text-green-700 dark:hover:text-green-300 transition-colors justify-end w-full sm:w-auto"
                >
                  <div className="text-right">
                    <div className="text-xs text-gray-500 dark:text-gray-400 mb-1">You've completed this lesson!</div>
                    <div className="font-medium">Complete Course</div>
                  </div>
                  <div className="bg-green-100 dark:bg-green-900/30 rounded-full p-2 ml-3 group-hover:bg-green-200 dark:group-hover:bg-green-900/50 transition-colors">
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </div>
                </Link>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LessonDetail;